// خط زمانی — grouping the BFF's derived `timeline` for the record's tab.
//
// The BFF answers a FLAT list of `TimelineEvent`s, newest first (core.md §6):
// encounter starts and ends, history entries, diagnoses, rx items, orders
// (ordered and resulted are two events) and thread starts. The client's only
// job is to fold them into the groups the tab renders — one per visit
// («ویزیت ۳ · date») plus «خارج از ویزیت» for anything not stamped — and to
// filter by type. Nothing here is stored and nothing here is derived from the
// record itself: the BFF is the one place that computes the timeline.
//
// Pure and React-free — tests/timeline.test.js calls it directly.

import {
  Stethoscope, FileText, ClipboardList, Pill, FlaskConical, FlaskRound, MessagesSquare, Flag,
} from 'lucide-react';

import { TIMELINE_TYPES } from '../constants/vocabulary.js';

export { TIMELINE_TYPES };

/** The icon for each event type. Line icons, drawn by the caller at 16px. */
export const TIMELINE_ICONS = {
  encounter_start: Stethoscope,
  encounter_end: Flag,
  entry: FileText,
  diagnosis: ClipboardList,
  rx: Pill,
  order: FlaskConical,
  order_result: FlaskRound,
  thread: MessagesSquare,
};

export const iconForType = (type) => TIMELINE_ICONS[type] || FileText;

/** The key for the «خارج از ویزیت» group. */
export const OUTSIDE_VISIT = '__outside__';

/**
 * Fold a flat, newest-first event list into groups.
 *
 * Groups keep the order of their NEWEST event, so a visit that is still being
 * written stays at the top even though it started before a later stray entry.
 * Every group carries its own `atMs` (its newest event) and, for a visit, the
 * `encounterSeq` the heading prints.
 */
export function groupTimeline(events) {
  const list = Array.isArray(events) ? events : [];
  const groups = new Map();
  for (const event of list) {
    const key = event?.encounterId || OUTSIDE_VISIT;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        encounterId: event?.encounterId || null,
        encounterSeq: event?.encounterSeq ?? null,
        atMs: event?.atMs || null,
        events: [],
      });
    }
    const group = groups.get(key);
    if (group.encounterSeq == null && event?.encounterSeq != null) group.encounterSeq = event.encounterSeq;
    group.events.push(event);
  }
  // Newest first, by each group's newest event — the list arrived that way, and
  // the first event a group received is its newest.
  return [...groups.values()].sort((a, b) => Number(b.atMs || 0) - Number(a.atMs || 0));
}

/** Keep only the selected types. An empty selection means "all". */
export function filterTimeline(events, types) {
  const wanted = Array.isArray(types) ? types.filter((x) => TIMELINE_TYPES.includes(x)) : [];
  if (!wanted.length) return Array.isArray(events) ? events : [];
  return (events || []).filter((e) => wanted.includes(e?.type));
}

/** The record tab and id an event links to, for click-through. */
export function targetOf(event, caseId) {
  if (!event) return null;
  if (event.type === 'thread') return { screen: 'chat', data: { caseId, threadId: event.refId } };
  if (event.encounterId) return { screen: 'visit', data: { caseId, encounterId: event.encounterId } };
  return { screen: 'patient', data: { caseId, tab: 'history' } };
}
